// ToastNotificationContext.jsx - Shared state for toast notifications
import React, { createContext, useContext, useState, useCallback } from 'react';
import { useUser } from '../../features/auth/hooks/useUser';
import { useNotifications } from '../../features/organization/hooks/useNotifications';

const ToastNotificationContext = createContext();

export const ToastNotificationProvider = ({ children }) => {
  const { userData } = useUser();
  const userId = userData?.userId || userData?.id || userData?.user_id;
  const { handleCreateNotification } = useNotifications(userId);

  const [notificationList, setNotificationList] = useState([]);

  const addNotification = useCallback(async (notification) => {
    const id = `${Date.now()}-${Math.random().toString(36).substring(2, 9)}`;
    const newNotification = {
      ...notification,
      id,
      read: false
    };

    // Add to local state for immediate UI feedback
    setNotificationList(currentList => [...currentList, newNotification]);

    // Try to create in backend (optional)
    try {
      if (userId && handleCreateNotification) {
        await handleCreateNotification({
          title: notification.title,
          description: notification.description,
          type: notification.type || 'INFO',
          sourceId: notification.sourceId || '',
          actionUrl: notification.actionUrl || ''
        });
      }
    } catch (err) {
      console.error('Failed to save notification to backend:', err);
    }
  }, [userId, handleCreateNotification]);

  const removeNotification = useCallback((idToRemove) => {
    setNotificationList(currentList => currentList.filter(n => n.id !== idToRemove));
  }, []);

  // Convenience methods
  const showSuccess = useCallback(async (title, description, actionUrl = '', sourceId = '') => {
    await addNotification({
      title,
      description,
      type: 'SUCCESS',
      actionUrl,
      sourceId
    });
  }, [addNotification]);

  const showError = useCallback(async (title, description, actionUrl = '', sourceId = '') => {
    await addNotification({
      title,
      description,
      type: 'ERROR',
      actionUrl,
      sourceId
    });
  }, [addNotification]);

  const showWarning = useCallback(async (title, description, actionUrl = '', sourceId = '') => {
    await addNotification({
      title,
      description,
      type: 'WARNING',
      actionUrl,
      sourceId
    });
  }, [addNotification]);

  const showInfo = useCallback(async (title, description, actionUrl = '', sourceId = '') => {
    await addNotification({
      title,
      description,
      type: 'INFO',
      actionUrl,
      sourceId
    });
  }, [addNotification]);

  // Local-only toast (no backend persistence)
  const showToastOnly = useCallback((title, description, type = 'INFO') => {
    const id = `${Date.now()}-${Math.random().toString(36).substring(2, 9)}`;
    setNotificationList(currentList => [...currentList, {
      id,
      title,
      description,
      type,
      read: false
    }]);
  }, []);

  return (
    <ToastNotificationContext.Provider value={{
      notifications: notificationList,
      addNotification,
      removeNotification,
      showSuccess,
      showError,
      showWarning,
      showInfo,
      showToastOnly
    }}>
      {children}
    </ToastNotificationContext.Provider>
  );
};

export const useToastNotificationContext = () => {
  const context = useContext(ToastNotificationContext);
  if (!context) {
    throw new Error('useToastNotificationContext must be used within ToastNotificationProvider');
  }
  return context;
};
